'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import { client, LEAD_TIME_SIGNAL_DAYS, LEAD_TIMES, UNFINISHED_YEARWORK } from '../demo-data'
import { activityLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import styles from './ai-chat-visual.module.css'
import { AppHeader, AppScreen } from './app-screen'
import { TypingDots } from './typing-dots'
import { useScenario } from './use-scenario'

// Both answers are read off the boards elsewhere on the page, so the assistant cannot claim
// anything the visuals beside it contradict.
const UNFINISHED = UNFINISHED_YEARWORK.length
const NOT_STARTED = UNFINISHED_YEARWORK.filter((row) => row.state === 'gepland')
const LONGEST_OPEN = LEAD_TIMES[0]
const DAYS_OVER = LONGEST_OPEN.days - LEAD_TIME_SIGNAL_DAYS

/**
 * A conversation that carries on, rather than one that is wiped and retyped.
 *
 * The old loop built both exchanges from an empty transcript and then reset to empty again, so
 * seven of its seventeen beats changed nothing and the card sat near-blank for a good part of every
 * pass — the emptiest frame on the page. The first exchange now never leaves. Only the follow-up is
 * asked again, so the sparsest frame the visual can reach is still a complete question and a
 * complete answer, and the answer carries its result rows rather than only asserting a number.
 */
const BEAT_HOLDS = [
  3200, // 0  both exchanges resolved — the answer landing on the wrap, then held. This is the
  //         resting frame, and the story's ending, which are the same image: giving the ending its
  //         own beat would render exactly what beat 0 already shows.
  650, //  1  the follow-up and its answer clear; the input bar is empty
  1000, // 2  the question types into the input bar, under the cursor
  550, //  3  the whole question sits in the bar, send lit
  700, //  4  sent: the bar clears and the question rises into the thread
  1100, // 5  the assistant thinks, and the answer lands as the loop returns to beat 0
] as const

const REST_BEAT = 0

const TYPING_FROM = 2
const TYPED_AT = 3
const SENT_FROM = 4
const THINKING_AT = 5

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive chat state. */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

/**
 * What the input bar holds at a given beat: nothing, half the question, or all of it.
 *
 * The question is typed here rather than appearing in a bubble that was already on the thread. The
 * bar was a still of an input — recognisable, and doing nothing, while a message it had no part in
 * assembled itself above it. A question that is typed where you would type it, and then leaves the
 * bar for the thread, is the same picture doing its own work.
 */
function draftText(t: VisualCopy, effBeat: number): string {
  if (effBeat < TYPING_FROM || effBeat >= SENT_FROM) return ''
  const full = t.chat.yearWork.followUp
  if (effBeat >= TYPED_AT) return full
  // A third of the line, then the whole of it. Half then whole read as two states rather than as
  // someone typing; a shorter first fragment makes the second one land as more words arriving.
  const words = full.split(' ')
  return words.slice(0, Math.max(2, Math.round(words.length / 3))).join(' ')
}

/** Small sparkle icon, accent stroke, inside an accent-soft rounded square. */
function SparkleIcon() {
  return (
    <span
      className="flex size-[28px] shrink-0 items-center justify-center rounded-[8px]"
      style={{ background: 'var(--au-accent-soft)' }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M8 1L9.4 6.6L15 8L9.4 9.4L8 15L6.6 9.4L1 8L6.6 6.6L8 1Z"
          stroke="var(--au-accent)"
          strokeWidth="1.1"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/** Right-arrow send icon for the input bar's accent send button. */
function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Bold entity or figure inside an answer, in the main text colour. */
function Name({ children }: { children: ReactNode }) {
  return (
    <b className="font-semibold" style={{ color: 'var(--au-text)' }}>
      {children}
    </b>
  )
}

function QuestionBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 justify-end">
      <div
        className="max-w-[88%] px-3 py-2 text-[13px] leading-snug font-medium text-white"
        style={{ background: 'var(--au-accent)', borderRadius: '14px 14px 4px 14px' }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * A message arriving on the thread, opening the height it needs and pushing everything above it up.
 *
 * `grid-template-rows: 0fr → 1fr` rather than a max-height guess: it animates to the row's own
 * height, so a one-line question and a six-line answer both take the same time to arrive and
 * neither is cut short or left waiting. The thread is bottom-aligned, so the space this opens comes
 * out of the top — which is the push.
 */
function Rising({ shown, children }: { shown: boolean; children: ReactNode }) {
  return (
    <div className={clsx(styles.rise, shown && styles.riseIn)} aria-hidden={!shown}>
      <div className={styles.riseInner}>{children}</div>
    </div>
  )
}

function AnswerBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 justify-start">
      <div
        className="max-w-[92%] px-3 py-2 text-[12px] leading-snug"
        style={{ background: 'var(--au-field)', color: 'var(--au-text2)', borderRadius: '14px 14px 14px 4px' }}
      >
        {children}
      </div>
    </div>
  )
}

function TypingBubble() {
  return (
    <div className="flex shrink-0 justify-start">
      <div className="rounded-[10px] rounded-tl-[3px] px-3 py-2.5" style={{ background: 'var(--au-field)' }}>
        <TypingDots />
      </div>
    </div>
  )
}

/**
 * Modules card — AI assistant chat window (MCP). The opening exchange never leaves; the follow-up
 * is typed into the input bar, sent, and rises onto the thread, pushing the exchange above it up.
 * `beat === null` (SSR, no-JS, reduced motion) renders the resolved rest state: both exchanges in
 * full, the bar back to its placeholder.
 */
export function AiChatVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  // The opening exchange is permanent: it is what keeps the transcript from ever being empty.
  const draft = draftText(t, effBeat)
  const caretVisible = effBeat >= TYPING_FROM && effBeat < SENT_FROM
  // On the thread from the beat it is sent, and on the resting frame it never left.
  const followUpVisible = effBeat === REST_BEAT || effBeat >= SENT_FROM
  const typingVisible = effBeat === THINKING_AT
  const answerVisible = effBeat === REST_BEAT

  return (
    <AppUiVisual label="AI-assistent via MCP" className={className}>
      <AppScreen>
        <div ref={ref} data-visual="assistant" className="flex h-full flex-col">
          <AppHeader
            leading={<SparkleIcon />}
            title="AI-assistent"
            right={
              <span
                className="rounded-[5px] px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
                style={{ background: 'var(--au-accent-soft)', color: 'var(--au-accent)' }}
              >
                MCP
              </span>
            }
          />

          {/* Bottom-aligned, the way a chat window actually sits: the thread grows upward from the
              input bar, so the resolved exchange always ends against it and whatever room is left
              over reads as history above rather than as a gap at the bottom. */}
          <div className="flex flex-1 flex-col justify-end gap-2.5 overflow-hidden px-4 py-3.5">
            <QuestionBubble>{t.chat.yearWork.question}</QuestionBubble>

            <AnswerBubble>
              <Name>
                {UNFINISHED} {t.word.clientsLower}
              </Name>{' '}
              {t.chat.yearWork.answerLead} <Name>{NOT_STARTED.length}</Name>{' '}
              {NOT_STARTED.length === 1 ? t.chat.yearWork.ofWhichOne : t.chat.yearWork.ofWhichMany}{' '}
              {NOT_STARTED.map((row, index) => (
                <span key={row.nr}>
                  {index > 0 && (index === NOT_STARTED.length - 1 ? t.chat.and : t.chat.comma)}
                  <Name>{client(row.nr).short}</Name>
                </span>
              ))}
              .
              {/* The clients themselves, under the sentence. An assistant that answers "5 klanten"
                  is making a claim; one that lists them is showing its work — and it fills a card
                  that used to be more white than content.
                  Names only: the assistant returns what it was asked for, and dressing each line
                  with a status pill would show it delivering something it cannot deliver today. */}
              <span className={styles.results}>
                {UNFINISHED_YEARWORK.slice(0, 5).map((row) => (
                  <span key={row.nr} className={styles.resultRow}>
                    {client(row.nr).short}
                  </span>
                ))}
              </span>
            </AnswerBubble>

            <Rising shown={followUpVisible}>
              <QuestionBubble>{t.chat.yearWork.followUp}</QuestionBubble>
            </Rising>

            <Rising shown={typingVisible}>
              <TypingBubble />
            </Rising>

            <Rising shown={answerVisible}>
              <AnswerBubble>
                <Name>{activityLabel(t, LONGEST_OPEN.activity) ?? LONGEST_OPEN.short}</Name> {t.word.of}{' '}
                <Name>{client(LONGEST_OPEN.nr).short}</Name> — {t.chat.yearWork.openFor}{' '}
                <Name>
                  {LONGEST_OPEN.days} {t.word.days}
                </Name>
                , {t.chat.yearWork.overThreshold(DAYS_OVER)}
              </AnswerBubble>
            </Rising>
          </div>

          {/* The input bar, doing the thing it looks like it does. */}
          <div className="flex items-center gap-2 border-t px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
            <div className={clsx(styles.input, draft && styles.inputFilled, 'min-w-0 flex-1 truncate')}>
              {draft || t.screen.aiChat.askPlanning}
              {caretVisible && <span aria-hidden className={styles.caret} />}
            </div>
            <span className={clsx(styles.send, draft && styles.sendArmed)}>
              <SendIcon />
            </span>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
